import { useEffect, useState } from "react";
import { Button, Card, Checkbox, Cluster, FormField, Grid, Input, Modal, NextBillNote, Stack } from "@salesapp/ui";
import { ApiError } from "../lib/api";
import { billingApi, type DiscountKickback, type OrgBilling, type PlanFeature, type PlanPrice } from "../lib/billingApi";
import {
  billedSeatsForActiveUsers,
  formatAckDate,
  moneyLabel,
  monthlyCents,
  planLabel,
  quoteCampaign,
  seatBandLabel,
} from "../lib/planPricing";
import { safePayfastCheckoutUrl } from "../lib/returnTo";
import { useAccess } from "../shell/AccessProvider";
import { useLocale } from "../shell/LocaleProvider";
import { AiBillingSection } from "./AiBillingSection";

const CARD_BRAND_LABELS: Record<string, string> = {
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "American Express",
  discover: "Discover",
};
function cardBrandLabel(brand?: string | null): string {
  if (!brand) return "Card";
  return CARD_BRAND_LABELS[brand.toLowerCase()] ?? "Card";
}

function errorWithReference(err: unknown, fallback: string): string {
  const message = err instanceof Error ? err.message : fallback;
  const errorId = err instanceof ApiError && err.body && typeof err.body === "object" && "error_id" in err.body
    ? (err.body as { error_id?: unknown }).error_id
    : null;
  return typeof errorId === "string" && errorId ? `${message} Reference: ${errorId}` : message;
}

function submitPayfastCheckout(processUrl: string, fields: Record<string, string>) {
  const form = document.createElement("form");
  form.method = "post";
  form.action = processUrl;
  form.style.display = "none";
  Object.entries(fields).forEach(([name, value]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
}

const PLAN_HIGHLIGHTS: Record<string, string[]> = {
  starter: [
    "Companies, contacts, and activity",
    "Lead capture",
    "Products",
    "Deal board",
    "Dashboards",
    "One territory",
  ],
  professional: [
    "Everything in Basic",
    "Stalled-deal alerts",
    "Workflow automation",
    "Quotes and orders",
    "Quote and order templates",
    "Sales-cycle checklists",
    "Forecast and support tickets",
  ],
  enterprise: [
    "Everything in Pro",
    "Invoices",
    "Custom screens (app designer)",
    "Extra sales territories",
    "IP allowlist for tenant access",
    "Timesheet",
    "Expense Manager",
    "Shared mailbox and meeting calendar",
  ],
};

const TERMS_URL = "https://saleslox.com/terms";
const EULA_URL = "https://saleslox.com/end-user-licence-agreement";
const PRIVACY_URL = "https://saleslox.com/privacy";
const DPA_URL = "https://saleslox.com/data-processing-agreement";

function planRank(tier: string | null | undefined): number {
  if (tier === "enterprise") return 3;
  if (tier === "professional") return 2;
  if (tier === "starter" || tier === "standard") return 1;
  return 0;
}

function FaceCard({
  brand,
  last4,
  name,
}: {
  brand?: string | null;
  last4?: string | null;
  name?: string | null;
}) {
  const brandName = cardBrandLabel(brand);
  return (
    <div className={`billing-face-card${brand && brand !== "unknown" ? ` is-${brand}` : ""}`} aria-hidden>
      <p className="billing-face-chip" />
      <p className="billing-face-number">{last4 ? `•••• •••• •••• ${last4}` : "•••• •••• •••• ••••"}</p>
      <Cluster gap={3} align="end" justify="between">
        <span className="billing-face-name">{name || "Cardholder"}</span>
        <span className="billing-face-brand">{last4 && brandName !== "Card" ? brandName.toUpperCase() : ""}</span>
      </Cluster>
    </div>
  );
}

function CompareTable({
  features,
  selectedTier,
  onChoose,
}: {
  features: PlanFeature[];
  selectedTier: string | null;
  onChoose: (tier: string) => void;
}) {
  const tiers = [
    { id: "starter" as const, label: "Basic" },
    { id: "professional" as const, label: "Pro" },
    { id: "enterprise" as const, label: "Enterprise" },
  ];
  return (
    <section className="billing-compare" id="billing-compare">
      <h2 className="home-section-title">Compare plans</h2>
      <p className="home-subtitle">What is on Basic, Pro, and Enterprise — the same list as the website.</p>
      <div className="billing-compare-wrap">
        <table className="billing-compare-table">
          <thead>
            <tr>
              <th>Feature</th>
              {tiers.map((tier) => (
                <th key={tier.id} className={selectedTier === tier.id ? "is-selected" : undefined}>
                  <button type="button" className="billing-compare-plan" onClick={() => onChoose(tier.id)}>
                    {tier.label}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {features.map((row) => (
              <tr key={row.id}>
                <th scope="row">
                  {row.label}
                  {row.coming ? <span className="home-subtitle"> · {row.coming}</span> : null}
                </th>
                {tiers.map((tier) => (
                  <td key={tier.id} data-plan={tier.label}>{row[tier.id] ? "Yes" : "—"}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="billing-compare-cards">
        {features.map((row) => (
          <article key={row.id} className="billing-compare-card">
            <h3>
              {row.label}
              {row.coming ? <span className="home-subtitle"> · {row.coming}</span> : null}
            </h3>
            <dl>
              {tiers.map((tier) => (
                <div key={tier.id}>
                  <dt>
                    <button
                      type="button"
                      className={`billing-compare-plan${selectedTier === tier.id ? " is-selected" : ""}`}
                      aria-label={`Choose ${tier.label}`}
                      onClick={() => onChoose(tier.id)}
                    >
                      {tier.label}
                    </button>
                  </dt>
                  <dd>{row[tier.id] ? "Yes" : "—"}</dd>
                </div>
              ))}
            </dl>
          </article>
        ))}
      </div>
    </section>
  );
}

export function BillingPanel({ orgId }: { orgId: string }) {
  const access = useAccess();
  const { formatDate, formatDateTime, timezone } = useLocale();
  const ackDate = (value: Date | string) => formatAckDate(value, timezone);
  const [billing, setBilling] = useState<OrgBilling | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelComment, setCancelComment] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [discountNote, setDiscountNote] = useState<string | null>(null);
  const [kickback, setKickback] = useState<DiscountKickback | null>(null);
  const [discountLocked, setDiscountLocked] = useState(false);
  const [view, setView] = useState<"overview" | "subscribe">("overview");
  const [compareOpen, setCompareOpen] = useState(false);
  const [focusPlan, setFocusPlan] = useState<PlanPrice | null>(null);
  const [interval, setInterval] = useState<"month" | "year">("month");
  const [pendingPlan, setPendingPlan] = useState<PlanPrice | null>(null);
  const [preview, setPreview] = useState<{
    plan_name: string;
    amount: string;
    currency: string;
    billed_at: string;
    renewal_at?: string;
    scheduled?: boolean;
  } | null>(null);
  const [lifecycleBusy, setLifecycleBusy] = useState(false);
  const [ack, setAck] = useState(false);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [verificationToken, setVerificationToken] = useState<string | null>(null);
  const [verifyBusy, setVerifyBusy] = useState(false);
  // cardMode is kept as a two-value enum for minimal diff even though "card_change" no
  // longer has a distinct capture UI — it now just drives beginPlanChange() against the
  // current plan (see the "Manage payment via PayFast" button below), and both modes share
  // the same confirmation-modal body once a card form is no longer part of the flow.
  const [cardMode, setCardMode] = useState<"plan_change" | "card_change" | null>(null);
  const [planChangeConfirmation, setPlanChangeConfirmation] = useState<{
    planName: string;
    amount: string;
    currency: string;
    billedAt: string;
    scheduled: boolean;
  } | null>(null);
  // The banner (planChangeConfirmation) stays as a durable on-page record; this only
  // gates the one-time popup so clicking OK doesn't also clear the banner beneath it.
  const [planChangePopupOpen, setPlanChangePopupOpen] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [downloadingReceiptId, setDownloadingReceiptId] = useState<string | null>(null);
  const [receiptError, setReceiptError] = useState<string | null>(null);
  const [payfastReturn, setPayfastReturn] = useState<"paid" | "cancelled" | "card_updated" | null>(null);

  async function downloadReceipt(eventId: string) {
    setDownloadingReceiptId(eventId);
    setReceiptError(null);
    try {
      await billingApi.downloadReceipt(orgId, eventId);
    } catch (err) {
      setReceiptError(err instanceof Error ? err.message : "Receipt could not be downloaded.");
    } finally {
      setDownloadingReceiptId(null);
    }
  }

  async function load() {
    try {
      const row = await billingApi.get(orgId);
      setBilling(row);
      if (!row.features?.length) {
        try {
          const catalogue = await billingApi.plans();
          if (catalogue?.features?.length) {
            setBilling({ ...row, features: catalogue.features });
          }
        } catch {
          /* compare table can stay empty until the catalogue is reachable */
        }
      }
    } catch (err) {
      setError(errorWithReference(err, "Billing could not be loaded."));
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get("payment");
    if (payment === "paid" || payment === "cancelled" || payment === "card_updated") {
      setPayfastReturn(payment);
      params.delete("payment");
      const next = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ""}${window.location.hash}`;
      window.history.replaceState({}, "", next);
    }
    void load();
  }, [orgId]);

  if (!billing) return error ? <p className="crm-error">{error}</p> : <p>Loading plan…</p>;

  // Platform-wide switch set by staff; the API refuses checkout too, this just says so up front.
  const paymentsAccepted = billing.payments_accepted !== false;
  const paymentsUnavailableCopy =
    billing.payments_accepted_reason?.trim() || "Payments are temporarily unavailable — try again shortly.";

  const currentBilling = billing;
  const activeLicenses = currentBilling.licenses.filter((row) => row.status === "active");
  const currentByApp = new Map(activeLicenses.map((row) => [row.app_code, row]));
  const crm = currentByApp.get("crm");
  const crmPlans = billing.plans.filter((plan) => plan.app_code === "crm");
  const otherPlans = crmPlans.filter((plan) => currentByApp.get(plan.app_code)?.tier !== plan.tier);
  const payments = (billing.plan_history ?? []).filter((row) => row.kind === "payment" || row.kind === "renewal");
  const currentPlan = crmPlans.find((plan) => plan.tier === crm?.tier) ?? null;
  const lastCrmLicense = billing.licenses.find((row) => row.app_code === "crm");
  const renewPlan = currentPlan ?? crmPlans.find((plan) => plan.tier === lastCrmLicense?.tier) ?? null;
  const activeUsers = billing.active_users ?? 0;
  const savedCard = billing.payment_method;
  const hasCard = Boolean(savedCard || billing.card_on_file);
  const onTrial = crm?.grant_source === "trial" || Boolean(access.isTrial);
  // Partner / billing-exempt licence: never invoiced, so every billing action (subscribe, change plan, update card,
  // cancel, resubscribe) is switched off here. The API refuses them too; this just says so up front.
  const billingExempt = crm?.grant_source === "billing_exempt";
  const billingLocked = !paymentsAccepted || billingExempt;
  // Expired / unpaid and the org's own admin can fix it by paying: lead with that instead of Cancel.
  const lapsed = Boolean(access.accessBlocked) && !billingExempt;
  const daysRemaining = access.daysRemaining;
  const features = billing.features ?? [];
  const selected = focusPlan ?? currentPlan;
  const daysLabel = daysRemaining == null
    ? null
    : `${daysRemaining} day${daysRemaining === 1 ? "" : "s"}`;

  function planTitle(plan: PlanPrice): string {
    return `${plan.display_name ?? planLabel(plan.tier)}`;
  }

  function resetChallenge() {
    setPendingPlan(null);
    setPreview(null);
    setAck(false);
    setChallengeId(null);
    setCode("");
    setVerificationToken(null);
    setCardMode(null);
    setVerifyBusy(false);
    setModalError(null);
  }

  function openSubscribe(plan: PlanPrice) {
    setError(null);
    setSuccess(null);
    if (billing?.cancellation && hasCard) {
      setError("Resubscribe before changing plan.");
      setView("overview");
      resetChallenge();
      return;
    }
    setFocusPlan(plan);
    setView("subscribe");
    setCompareOpen(false);
    setInterval(plan.campaign ? "year" : "month");
    setDiscountLocked(false);
    setKickback(null);
    setDiscountNote(null);
    setPlanChangeConfirmation(null);
    setPlanChangePopupOpen(false);
    resetChallenge();
  }

  function managePaymentMethod() {
    if (!billing) return;
    setError(null);
    setSuccess(null);
    const updateUrl = safePayfastCheckoutUrl(billing.payment_update_url);
    if (updateUrl) {
      window.location.assign(updateUrl);
      return;
    }
    if (currentPlan) openSubscribe(currentPlan);
  }

  function backToOverview() {
    setView("overview");
    setCompareOpen(false);
    setFocusPlan(null);
    resetChallenge();
  }

  async function startCode(purpose: "plan_change" | "card_change") {
    setVerifyBusy(true);
    setError(null);
    setModalError(null);
    try {
      const started = await billingApi.startVerification(orgId, purpose);
      setChallengeId(started.challenge_id);
      setCode("");
      setVerificationToken(null);
    } catch (err) {
      setModalError(err instanceof Error ? err.message : "The confirmation code could not be sent.");
    } finally {
      setVerifyBusy(false);
    }
  }

  async function confirmCode(purpose: "plan_change" | "card_change") {
    if (!challengeId) return null;
    setVerifyBusy(true);
    setError(null);
    setModalError(null);
    try {
      const confirmed = await billingApi.confirmVerification(orgId, {
        purpose,
        challenge_id: challengeId,
        code,
      });
      setVerificationToken(confirmed.verification_token);
      return confirmed.verification_token;
    } catch (err) {
      setModalError(err instanceof Error ? err.message : "That confirmation code is not valid.");
      return null;
    } finally {
      setVerifyBusy(false);
    }
  }

  async function beginPlanChange(plan: PlanPrice) {
    if (billing?.cancellation && hasCard) {
      setModalError(null);
      setPendingPlan(null);
      setCardMode(null);
      setError("Resubscribe before changing plan.");
      return;
    }
    const seats = billedSeatsForActiveUsers(plan, activeUsers);
    if (seats == null) {
      setError(
        `${planTitle(plan)} is for ${seatBandLabel(plan)}. You have ${activeUsers} active users. Disable extra people before switching — only active users count toward the licence.`,
      );
      return;
    }
    setError(null);
    setModalError(null);
    setPendingPlan(plan);
    setAck(false);
    setCardMode("plan_change");
    try {
      const quoted = await billingApi.previewPlan(orgId, {
        tier: plan.tier,
        seats,
        interval,
        discount_code: discountCode.trim() || null,
      });
      setPreview(quoted);
      await startCode("plan_change");
    } catch (err) {
      setPendingPlan(null);
      setCardMode(null);
      setError(err instanceof Error ? err.message : "That plan could not be quoted.");
    }
  }

  async function runLifecycle(action: () => Promise<OrgBilling>, failure: string, successMessage?: string) {
    setLifecycleBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const row = await action();
      setBilling(row);
      setSuccess(row.notice || successMessage || null);
      resetChallenge();
      setView("overview");
      setCompareOpen(false);
      setFocusPlan(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : failure);
    } finally {
      setLifecycleBusy(false);
    }
  }

  async function finishPlan(token: string) {
    if (!pendingPlan) return;
    const seats = billedSeatsForActiveUsers(pendingPlan, activeUsers);
    if (seats == null) return;
    setVerifyBusy(true);
    try {
      const row = await billingApi.checkout(orgId, {
        app_code: pendingPlan.app_code,
        tier: pendingPlan.tier,
        seats,
        interval,
        campaign_id: null,
        discount_code: discountCode.trim() || null,
        verification_token: token,
        acknowledged: true,
      });
      const processUrl = safePayfastCheckoutUrl(row.checkout_process_url);
      if (processUrl && row.checkout_fields) {
        setSuccess("Redirecting to secure PayFast checkout...");
        submitPayfastCheckout(processUrl, row.checkout_fields);
        const checkout = safePayfastCheckoutUrl(row.checkout_url);
        if (checkout) {
          window.setTimeout(() => {
            window.location.assign(checkout);
          }, 1200);
        }
        return;
      }
      const checkout = safePayfastCheckoutUrl(row.checkout_url);
      if (checkout) {
        setSuccess("Redirecting to secure PayFast checkout...");
        window.location.assign(checkout);
        return;
      }
      // Capture the confirmation before resetChallenge() clears preview/pendingPlan below —
      // this is the only on-screen record of what plan, amount, and date the customer just agreed to.
      if (preview) {
        setPlanChangeConfirmation({
          planName: preview.plan_name,
          amount: preview.amount,
          currency: preview.currency,
          billedAt: preview.billed_at,
          scheduled: Boolean(preview.scheduled || row.pending_plan),
        });
        setPlanChangePopupOpen(true);
      }
      setBilling(row);
      resetChallenge();
      setView("overview");
      setCompareOpen(false);
      setFocusPlan(null);
    } catch (err) {
      setModalError(err instanceof Error ? err.message : "Checkout failed");
      setError(err instanceof Error ? err.message : "Checkout failed");
    } finally {
      setVerifyBusy(false);
    }
  }

  async function onVerifyAndContinue() {
    const purpose = cardMode ?? "plan_change";
    if (!ack) {
      setModalError("Tick the confirmation before continuing.");
      return;
    }
    const token = verificationToken ?? await confirmCode(purpose);
    if (!token) return;
    await finishPlan(token);
  }

  function unlockDiscount() {
    setDiscountLocked(false);
    setKickback(null);
    setDiscountNote(null);
  }

  async function applyDiscount(plan: PlanPrice | undefined, seats: number) {
    const codeValue = discountCode.trim();
    if (!codeValue) {
      setKickback(null);
      setDiscountNote(null);
      setDiscountLocked(false);
      return;
    }
    if (!plan) {
      setDiscountNote("Choose a plan first.");
      return;
    }
    setError(null);
    try {
      const quoted = await billingApi.previewDiscount({
        code: codeValue,
        tier: plan.tier,
        interval,
        seats,
        amount_cents: interval === "year" ? monthlyCents(plan, seats) * 12 : monthlyCents(plan, seats),
        email: currentBilling.billing_email,
      });
      setKickback(quoted);
      setDiscountNote(quoted.hook);
      setDiscountLocked(true);
    } catch (err) {
      setKickback(null);
      setDiscountLocked(false);
      setDiscountNote(err instanceof Error ? err.message : "That discount code is not valid.");
    }
  }

  function kickbackForList(listCents: number): { pay: number; save: number } | null {
    if (!kickback) return null;
    if (kickback.interval && kickback.interval !== interval) return null;
    if (kickback.list_cents === listCents) {
      return { pay: kickback.pay_cents, save: kickback.save_cents };
    }
    const pct = kickback.percent_off;
    if (pct != null && Number.isFinite(pct) && pct > 0) {
      const pay = Math.round(listCents * (100 - pct) / 100);
      return { pay, save: Math.max(0, listCents - pay) };
    }
    return null;
  }

  function chooseFromCompare(tier: string) {
    const plan = crmPlans.find((item) => item.tier === tier);
    if (!plan || !paymentsAccepted) return;
    openSubscribe(plan);
  }

  const ackLabel = preview
    ? `I confirm I am changing to the plan ${preview.plan_name} and my next payment will be the amount of ${moneyLabel(Math.round(Number(preview.amount) * 100), preview.currency)} on the ${ackDate(preview.billed_at)}.${preview.scheduled ? ` The change takes effect on ${ackDate(preview.renewal_at || preview.billed_at)}; until then I keep my current plan.` : ""}`
    : "";

  const subscribeSeats = selected ? billedSeatsForActiveUsers(selected, activeUsers) : null;
  const subscribeQuoted = selected && subscribeSeats != null
    ? quoteCampaign(monthlyCents(selected, subscribeSeats), selected.campaign)
    : null;
  const subscribeCurrency = selected?.local_currency || selected?.currency || "USD";
  const subscribeListCents = selected && subscribeSeats != null
    ? (interval === "year" ? monthlyCents(selected, subscribeSeats) * 12 : monthlyCents(selected, subscribeSeats))
    : 0;
  const subscribeApplied = kickbackForList(subscribeListCents);
  const planChangesBlocked = Boolean(billing.cancellation && hasCard);

  return (
    <Stack gap={4}>
      {payfastReturn === "paid" ? (
        <Card>
          <Stack gap={2}>
            <p className="pay-card-success" role="status">Payment received.</p>
            <p className="home-subtitle">
              PayFast is confirming the payment with Saleslox. Your plan and receipt will update here shortly.
            </p>
            <Cluster gap={3} align="center">
              <Button type="button" variant="secondary" onClick={() => { setPayfastReturn(null); return load(); }}>
                Refresh billing
              </Button>
              <Button type="button" variant="ghost" onClick={() => setPayfastReturn(null)}>
                Dismiss
              </Button>
            </Cluster>
          </Stack>
        </Card>
      ) : payfastReturn === "card_updated" ? (
        <Card>
          <Stack gap={2}>
            <p className="pay-card-success" role="status">Returned from PayFast card update.</p>
            <p className="home-subtitle">
              If the update was completed, PayFast will use the new card for future renewals. Refresh billing to check the saved card details.
            </p>
            <Cluster gap={3} align="center">
              <Button type="button" variant="secondary" onClick={() => { setPayfastReturn(null); return load(); }}>
                Refresh billing
              </Button>
              <Button type="button" variant="ghost" onClick={() => setPayfastReturn(null)}>
                Dismiss
              </Button>
            </Cluster>
          </Stack>
        </Card>
      ) : payfastReturn === "cancelled" ? (
        <Card>
          <Stack gap={2}>
            <p className="crm-error" role="alert">Payment was not completed.</p>
            <p className="home-subtitle">PayFast returned without completing the payment. You were not charged by this checkout session.</p>
            <Cluster gap={3} align="center">
              <Button type="button" variant="secondary" onClick={() => setPayfastReturn(null)}>
                Dismiss
              </Button>
            </Cluster>
          </Stack>
        </Card>
      ) : null}
      {error && !pendingPlan && cardMode == null && <p className="crm-error">{error}</p>}
      {success ? <p className="pay-card-success" role="status">{success}</p> : null}
      {!paymentsAccepted && !onTrial ? <p className="crm-error">{paymentsUnavailableCopy}</p> : null}
      {lapsed ? (
        <Card>
          <Stack gap={3}>
            <p className="billing-trial-copy" role="status">
              Your subscription has expired. Please pay for a subscription to enable the apps.
            </p>
            {renewPlan ? (
              <Cluster gap={3} align="center">
                <Button type="button" disabled={billingLocked} onClick={() => openSubscribe(renewPlan)}>
                  {`Pay for ${planTitle(renewPlan)}`}
                </Button>
                <span className="home-subtitle">Or choose a different plan below.</span>
              </Cluster>
            ) : (
              <p className="home-subtitle">Choose a plan below to pay and enable the apps.</p>
            )}
          </Stack>
        </Card>
      ) : null}
      {billingExempt ? (
        <Card>
          <p className="home-subtitle" role="status">
            {`Partner licence: billing does not apply to this organization, so plan changes, payment details and cancellation are switched off.${
              crm?.access_ends_at ? ` Access runs until ${formatDate(crm.access_ends_at)}.` : ""
            }`}
          </p>
        </Card>
      ) : null}
      {planChangeConfirmation ? (
        <Card>
          <Stack gap={2}>
            <p className="pay-card-success" role="status">
              {planChangeConfirmation.scheduled
                ? `Scheduled: you move to ${planChangeConfirmation.planName} on ${ackDate(planChangeConfirmation.billedAt)}. You keep your current plan until then.`
                : `You're now on ${planChangeConfirmation.planName}.`}
            </p>
            <p className="home-subtitle">
              {`Next amount to be billed: ${moneyLabel(Math.round(Number(planChangeConfirmation.amount) * 100), planChangeConfirmation.currency)} on ${ackDate(planChangeConfirmation.billedAt)}.`}
            </p>
            <Cluster gap={3} align="center">
              <Button type="button" variant="secondary" onClick={() => setPlanChangeConfirmation(null)}>
                Dismiss
              </Button>
            </Cluster>
          </Stack>
        </Card>
      ) : null}
      <Modal
        isOpen={planChangePopupOpen && Boolean(planChangeConfirmation)}
        onClose={() => setPlanChangePopupOpen(false)}
        title="Plan change confirmed"
        footer={(
          <Button type="button" onClick={() => setPlanChangePopupOpen(false)}>
            OK
          </Button>
        )}
      >
        {planChangeConfirmation ? (
          <Stack gap={2}>
            <p>
              {planChangeConfirmation.scheduled
                ? `Scheduled: you move to ${planChangeConfirmation.planName} on ${ackDate(planChangeConfirmation.billedAt)}. You keep your current plan until then.`
                : `You're now on ${planChangeConfirmation.planName}.`}
            </p>
            <p className="home-subtitle">
              {`Next amount to be billed: ${moneyLabel(Math.round(Number(planChangeConfirmation.amount) * 100), planChangeConfirmation.currency)} on ${ackDate(planChangeConfirmation.billedAt)}.`}
            </p>
          </Stack>
        ) : null}
      </Modal>
      <Card>
        <div className="billing-current">
          <Stack gap={3}>
            <p className="plan-kicker">Current plan</p>
            <h2 className="home-section-title">{crm ? `CRM ${planLabel(crm.tier ?? "")}` : "No CRM plan"}</h2>
            <p className="home-subtitle">
              {crm?.access_ends_at
                ? `Access through ${formatDate(crm.access_ends_at)}.`
                : "No end date on file."}
              {` · ${activeUsers} active users`}
            </p>
            {activeLicenses.filter((row) => row.app_code !== "crm").map((row) => (
              <p key={row.app_code} className="home-subtitle">
                Add-on: {row.app_code} {row.tier}
                {row.access_ends_at ? ` · through ${formatDate(row.access_ends_at)}` : ""}
              </p>
            ))}
            {billing.cancellation ? (
              <Stack gap={2}>
                <p className="home-subtitle">
                  {`Cancelled — you keep access until ${ackDate(billing.cancellation.access_until)}. You will not be charged again.`}
                </p>
                {hasCard ? (
                  <Cluster gap={3} align="center">
                    <Button
                      type="button"
                      disabled={lifecycleBusy || billingLocked}
                      onClick={() => runLifecycle(() => billingApi.resubscribe(orgId), "Resubscribe failed", "Subscription resumed.")}
                    >
                      Resubscribe
                    </Button>
                  </Cluster>
                ) : (
                  <p className="home-subtitle">Choose a plan below to restart billing with a new PayFast checkout.</p>
                )}
              </Stack>
            ) : null}
            {billing.pending_plan && !billing.cancellation ? (
              <Stack gap={2}>
                <p className="home-subtitle">
                  {`Scheduled: ${billing.pending_plan.plan_name} · ${billing.pending_plan.seats} users, billed ${billing.pending_plan.interval === "year" ? "yearly" : "monthly"} from ${ackDate(billing.pending_plan.effective_at)}. Until then you keep your current plan.`}
                </p>
                <Cluster gap={3} align="center">
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={lifecycleBusy || billingLocked}
                    onClick={() => runLifecycle(() => billingApi.keepCurrentPlan(orgId), "Could not keep the current plan", "Scheduled plan change removed.")}
                  >
                    Keep current plan
                  </Button>
                </Cluster>
              </Stack>
            ) : null}
            {view === "overview" && onTrial && currentPlan ? (
              <Stack gap={3} className="billing-trial-prompt">
                <p className="billing-trial-copy">
                  {!paymentsAccepted
                    ? paymentsUnavailableCopy
                    : daysLabel
                      ? `Your trial expires in ${daysLabel}. Click on Subscribe to continue using the solution.`
                      : "Your trial is active. Click on Subscribe to continue using the solution."}
                </p>
                <Grid columns={2} gap={3} className="billing-trial-actions">
                  <Button
                    type="button"
                    fullWidth
                    disabled={billingLocked}
                    onClick={() => openSubscribe(currentPlan)}
                  >
                    Subscribe
                  </Button>
                  {lapsed ? null : (
                    <Button type="button" variant="danger" fullWidth disabled={billingLocked} onClick={() => setCancelOpen(true)}>
                      Cancel subscription
                    </Button>
                  )}
                </Grid>
              </Stack>
            ) : null}
            {view === "overview" && planChangesBlocked ? (
              <p className="home-subtitle">Resubscribe before changing plan.</p>
            ) : null}
            <Cluster gap={3} align="center">
              {view === "subscribe" ? (
                <Button type="button" variant="secondary" onClick={() => backToOverview()}>Back</Button>
              ) : onTrial || billing.cancellation || lapsed ? null : (
                <Button type="button" variant="danger" disabled={billingLocked} onClick={() => setCancelOpen(true)}>
                  Cancel subscription
                </Button>
              )}
            </Cluster>
          </Stack>
          <Stack gap={2} className="billing-card-on-file">
            <FaceCard
              brand={savedCard?.brand}
              last4={savedCard?.last4}
              name={billing.display_name}
            />
            <Cluster gap={3} align="center" justify="between">
              {savedCard ? (
                <p className="saved-card-mask">
                  {`${cardBrandLabel(savedCard.brand)} ending ${savedCard.last4}`}
                </p>
              ) : hasCard ? (
                <p className="home-subtitle">Card saved securely with PayFast</p>
              ) : (
                <p className="home-subtitle">No card on file</p>
              )}
              <Button
                type="button"
                variant="secondary"
                disabled={!currentPlan || billingLocked}
                onClick={() => managePaymentMethod()}
              >
                Manage payment via PayFast
              </Button>
            </Cluster>
          </Stack>
          {billing.next_bill ? (
            <NextBillNote
              amount={billing.next_bill.amount}
              currency={billing.next_bill.currency}
              billedAt={billing.next_bill.billed_at}
              renewalAt={billing.next_bill.renewal_at}
            />
          ) : null}
        </div>
      </Card>
      {view === "subscribe" && selected ? (
        <Card>
          <Stack gap={4}>
            <p className="plan-kicker">{seatBandLabel(selected)}</p>
            <h2 className="home-section-title">{planTitle(selected)}</h2>
            {selected.campaign && interval === "year" ? <p className="plan-hook">{selected.campaign.hook}</p> : null}
            <ul className="billing-plan-features">
              {(PLAN_HIGHLIGHTS[selected.tier] ?? []).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <fieldset className="billing-interval">
              <legend>Billing interval</legend>
              <label className="billing-interval-choice">
                <input
                  type="radio"
                  name="billing-interval"
                  value="month"
                  checked={interval === "month"}
                  onChange={() => { setInterval("month"); unlockDiscount(); }}
                />
                Monthly
              </label>
              <label className="billing-interval-choice">
                <input
                  type="radio"
                  name="billing-interval"
                  value="year"
                  checked={interval === "year"}
                  onChange={() => { setInterval("year"); unlockDiscount(); }}
                />
                Yearly
              </label>
            </fieldset>
            {subscribeQuoted ? (
              <Stack gap={2}>
                <p className="home-subtitle">
                  Monthly {moneyLabel(subscribeQuoted.monthlyCents, subscribeCurrency)}
                  {subscribeSeats ? ` · ${subscribeSeats} billed seats` : ""}
                </p>
                {interval === "year" ? (
                  <p className="plan-yearly">
                    {subscribeApplied ? (
                      <>
                        Yearly <s className="plan-price-was">{moneyLabel(subscribeQuoted.yearlyListCents, subscribeCurrency)}</s>
                        {" "}pay {moneyLabel(subscribeApplied.pay, subscribeCurrency)} now
                        <span className="plan-save"> save {moneyLabel(subscribeApplied.save, subscribeCurrency)}</span>
                      </>
                    ) : subscribeQuoted.saveCents > 0 ? (
                      <>
                        Yearly <s className="plan-price-was">{moneyLabel(subscribeQuoted.yearlyListCents, subscribeCurrency)}</s>
                        {" "}pay {moneyLabel(subscribeQuoted.payNowCents, subscribeCurrency)} now
                        <span className="plan-save"> save {moneyLabel(subscribeQuoted.saveCents, subscribeCurrency)}</span>
                      </>
                    ) : (
                      <>Yearly {moneyLabel(subscribeQuoted.yearlyListCents, subscribeCurrency)}</>
                    )}
                  </p>
                ) : (
                  <p className="plan-yearly">
                    {subscribeApplied
                      ? <>Pay {moneyLabel(subscribeApplied.pay, subscribeCurrency)} now</>
                      : <>Billed monthly {moneyLabel(subscribeQuoted.monthlyCents, subscribeCurrency)}</>}
                  </p>
                )}
              </Stack>
            ) : (
              <p className="crm-error">
                {`${planTitle(selected)} is for ${seatBandLabel(selected)}. You have ${activeUsers} active users. Disable extra people before switching — only active users count toward the licence.`}
              </p>
            )}
            <FormField
              label="Discount code"
              htmlFor="billing-discount-code"
              hint="Optional. The plan still bills the full per-user price; a valid code kicks back a percent or buy-12-get-free months on this payment only."
            >
              <Stack gap={2}>
                <Cluster gap={2} align="end">
                  <Input
                    id="billing-discount-code"
                    value={discountCode}
                    disabled={discountLocked}
                    onChange={(event) => {
                      setDiscountCode(event.target.value);
                      if (!discountLocked) {
                        setKickback(null);
                        setDiscountNote(null);
                      }
                    }}
                    autoComplete="off"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={discountLocked || subscribeSeats == null}
                    onClick={() => applyDiscount(selected, subscribeSeats ?? selected.min_seats ?? 5)}
                  >
                    Apply discount code
                  </Button>
                </Cluster>
                {discountLocked ? (
                  <Button type="button" variant="ghost" onClick={() => unlockDiscount()}>
                    Change discount code
                  </Button>
                ) : null}
              </Stack>
            </FormField>
            {discountNote ? <p className={kickback ? "plan-save" : "crm-error"}>{discountNote}</p> : null}
            {!paymentsAccepted ? (
              <p className="crm-error">{paymentsUnavailableCopy}</p>
            ) : planChangesBlocked ? (
              <p className="crm-error">Resubscribe before changing plan.</p>
            ) : !hasCard ? (
              <p className="home-subtitle">
                You will confirm the plan, then continue to PayFast to complete payment.
              </p>
            ) : null}
            <p className="home-subtitle">We accept Visa and Mastercard credit cards only.</p>
            <Cluster gap={3} align="center">
              <Button
                type="button"
                disabled={subscribeSeats == null || billingLocked || planChangesBlocked}
                onClick={() => beginPlanChange(selected)}
              >
                {interval === "year"
                  ? subscribeApplied
                    ? `Subscribe · ${moneyLabel(subscribeApplied.pay, subscribeCurrency)}`
                    : "Subscribe yearly"
                  : "Subscribe monthly"}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setCompareOpen((open) => !open)}
              >
                {compareOpen ? "Hide comparison" : "Compare"}
              </Button>
            </Cluster>
          </Stack>
        </Card>
      ) : null}
      {view === "overview" && otherPlans.length > 0 ? (
        <Stack gap={3}>
          <h2 className="home-section-title">Change plan</h2>
          <div className="plan-grid">
            {otherPlans.map((plan) => {
              const current = currentByApp.get(plan.app_code);
              const isUpgrade = !current || planRank(plan.tier) > planRank(current.tier);
              const count = billedSeatsForActiveUsers(plan, activeUsers) ?? (plan.min_seats ?? plan.seats);
              const quoted = quoteCampaign(monthlyCents(plan, count), plan.campaign);
              const currency = plan.local_currency || plan.currency;
              return (
                <Card key={`${plan.app_code}-${plan.tier}`}>
                  <Stack gap={3}>
                    <p className="plan-kicker">{isUpgrade ? "Upgrade" : "Downgrade"} · {seatBandLabel(plan)}</p>
                    <strong>{planTitle(plan)}</strong>
                    {plan.campaign && <p className="plan-hook">{plan.campaign.hook}</p>}
                    <p className="home-subtitle">Monthly {moneyLabel(quoted.monthlyCents, currency)}</p>
                    <p className="plan-yearly">
                      {quoted.saveCents > 0 ? (
                        <>
                          Yearly <s className="plan-price-was">{moneyLabel(quoted.yearlyListCents, currency)}</s>
                          {" "}pay {moneyLabel(quoted.payNowCents, currency)} now
                          <span className="plan-save"> save {moneyLabel(quoted.saveCents, currency)}</span>
                        </>
                      ) : (
                        <>Yearly {moneyLabel(quoted.yearlyListCents, currency)}</>
                      )}
                    </p>
                    <Button
                      type="button"
                      variant={isUpgrade ? "primary" : "secondary"}
                      disabled={billingLocked}
                      onClick={() => openSubscribe(plan)}
                    >
                      {isUpgrade ? "Upgrade" : "Switch to this plan"}
                    </Button>
                  </Stack>
                </Card>
              );
            })}
          </div>
        </Stack>
      ) : null}
      {view === "overview" ? (
        <Stack gap={3}>
          <h2 className="home-section-title">Payment history</h2>
          {receiptError ? <p className="crm-error" role="alert">{receiptError}</p> : null}
          {payments.length === 0 ? (
            <p className="home-subtitle">No payments yet.</p>
          ) : (
            <Card>
              <table className="billing-payment-history">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Plan</th>
                    <th>Amount</th>
                    <th aria-label="Receipt" />
                  </tr>
                </thead>
                <tbody>
                  {payments.map((row) => (
                    <tr key={row.event_id}>
                      <td>
                        {row.created_at ? formatDateTime(row.created_at) : "—"}
                      </td>
                      <td>
                        {row.to_tier ? planLabel(row.to_tier) : "—"}
                        {row.seats ? ` · ${row.seats} seats` : ""}
                      </td>
                      <td>
                        {row.amount_cents != null && row.currency
                          ? moneyLabel(row.amount_cents, row.currency)
                          : "—"}
                      </td>
                      <td>
                        {row.receipt_available ? (
                          <Button
                            type="button"
                            variant="secondary"
                            disabled={downloadingReceiptId === row.event_id}
                            onClick={() => downloadReceipt(row.event_id)}
                          >
                            {downloadingReceiptId === row.event_id ? "Downloading…" : "Download receipt"}
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </Stack>
      ) : null}
      {view === "overview" ? <AiBillingSection orgId={orgId} locked={billingLocked} /> : null}
      {view === "subscribe" && compareOpen ? (
        <CompareTable
          features={features}
          selectedTier={selected?.tier ?? null}
          onChoose={chooseFromCompare}
        />
      ) : null}
      <Modal
        isOpen={Boolean(pendingPlan) || cardMode === "card_change"}
        onClose={() => { if (!verifyBusy) resetChallenge(); }}
        title="Confirm plan change"
        footer={(
          <>
            <Button type="button" variant="secondary" disabled={verifyBusy} onClick={() => resetChallenge()}>
              Cancel
            </Button>
            <Button type="button" disabled={verifyBusy || !ack} onClick={() => onVerifyAndContinue()}>
              {verifyBusy ? "Working…" : verificationToken ? "Continue" : "Confirm code"}
            </Button>
          </>
        )}
      >
        <Stack gap={4}>
          {modalError ? <p className="crm-error" role="alert">{modalError}</p> : null}
          {preview ? (
            <Checkbox
              label={ackLabel}
              checked={ack}
              onChange={(event) => setAck(event.target.checked)}
            />
          ) : (
            <p className="home-subtitle">
              We emailed a 6-digit code, valid for 5 minutes, before continuing to PayFast. We accept Visa and Mastercard credit cards only.
            </p>
          )}
          <FormField label="Confirmation code" htmlFor="billing-verify-code" hint="Check the inbox for a 6-digit code. It expires in 5 minutes.">
            <Input
              id="billing-verify-code"
              className="verify-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
              disabled={Boolean(verificationToken)}
            />
          </FormField>
          <p className="billing-legal">
            By confirming you agree to the{" "}
            <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">Terms and Conditions</a>
            {", the "}
            <a href={EULA_URL} target="_blank" rel="noopener noreferrer">End User Licence Agreement</a>
            {", the "}
            <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">Privacy Policy</a>
            {", and the "}
            <a href={DPA_URL} target="_blank" rel="noopener noreferrer">Data Processing Agreement</a>.
          </p>
        </Stack>
      </Modal>
      <Modal
        isOpen={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title="We're sorry to see you go"
        footer={(
          <>
            <Button type="button" variant="secondary" onClick={() => setCancelOpen(false)}>Keep my plan</Button>
            <Button
              type="button"
              variant="danger"
              disabled={cancelling}
              onClick={() => {
                setCancelling(true);
                setError(null);
                setSuccess(null);
                void billingApi.cancel(orgId, false, cancelComment).then((row) => {
                  setBilling(row);
                  setSuccess(row.notice || "Subscription cancelled. You will keep access until the end of the paid term.");
                  setCancelOpen(false);
                  setCancelComment("");
                }).catch((err) => {
                  setError(err instanceof Error ? err.message : "Cancel failed");
                }).finally(() => setCancelling(false));
              }}
            >
              {cancelling ? "Cancelling…" : "Cancel subscription"}
            </Button>
          </>
        )}
      >
        <Stack gap={3}>
          <p className="home-subtitle">
            {onTrial
              ? "Cancelling ends your trial now."
              : `You keep access until ${crm?.access_ends_at ? ackDate(crm.access_ends_at) : "the end of the period you have paid for"}, and you will not be charged again. Choose a plan to start a new PayFast subscription.`}
          </p>
          <p className="home-subtitle">
            Before you go, tell us about your experience and why you are cancelling. Your note helps us improve the product for teams like yours.
          </p>
          <label>
            Optional comment
            <textarea
              className="cancel-comment"
              value={cancelComment}
              onChange={(event) => setCancelComment(event.target.value)}
              rows={5}
              maxLength={2000}
              placeholder="What should we have done better?"
            />
          </label>
        </Stack>
      </Modal>
    </Stack>
  );
}
